import { useSyncExternalStore } from "react";

import type { ActiveMarketView } from "@/types/market-intelligence";

/**
 * Whether Market Intelligence's "My views" panel is open — the column the
 * top bar's bookmark opens at the right of the page — and the saved view the
 * chart on screen is showing, for that panel to mark.
 *
 * Two tiny external stores rather than React context: the panel and its
 * button live in the shell, the charts that report their view live in pages,
 * and neither side is wrapped around the other. The panel starts closed and
 * is remembered across reloads, so a reader who keeps it open keeps it open.
 */

const OPEN_KEY = "mi-my-views-open";

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

let open = readOpen();
const openListeners = new Set<() => void>();

export function setMyViewsPanelOpen(next: boolean) {
  if (next === open) return;
  open = next;
  try {
    if (next) localStorage.setItem(OPEN_KEY, "1");
    else localStorage.removeItem(OPEN_KEY);
  } catch {
    // Blocked storage: the panel still works for this visit.
  }
  openListeners.forEach((l) => l());
}

function subscribeOpen(listener: () => void) {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

export function useMyViewsPanelOpen(): boolean {
  return useSyncExternalStore(subscribeOpen, () => open, () => false);
}

let active: ActiveMarketView | null = null;
const activeListeners = new Set<() => void>();

function sameActive(a: ActiveMarketView | null, b: ActiveMarketView | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.scope === b.scope && a.hubCode === b.hubCode && a.viewId === b.viewId && a.dirty === b.dirty
  );
}

/** Called by `useMarketView` as the chart on screen changes view. */
export function publishActiveMarketView(next: ActiveMarketView | null) {
  if (sameActive(active, next)) return;
  active = next;
  activeListeners.forEach((l) => l());
}

function subscribeActive(listener: () => void) {
  activeListeners.add(listener);
  return () => {
    activeListeners.delete(listener);
  };
}

export function useActiveMarketView(): ActiveMarketView | null {
  return useSyncExternalStore(subscribeActive, () => active, () => null);
}
